<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg">
    <!-- Header -->
    <div class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200">
      <div>
        <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">Messages</h2>
        <p v-if="!loading" class="tw-text-sm tw-text-gray-600">
          {{ conversations.length }} conversation{{ conversations.length !== 1 ? 's' : '' }}
        </p>
        <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
      </div>
      
      <!-- Search (optional enhancement) -->
      <div class="tw-flex tw-items-center tw-gap-2">
        <button
          @click="refreshConversations"
          :disabled="loading"
          class="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
          title="Refresh conversations"
        >
          <icon icon="solar:refresh-bold-duotone" class="tw-text-lg" />
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="tw-flex-1 tw-p-4 tw-space-y-3">
      <div v-for="i in 5" :key="i" class="tw-flex tw-items-center tw-gap-3 tw-p-3">
        <div class="tw-w-12 tw-h-12 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
        <div class="tw-flex-1 tw-space-y-2">
          <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
          <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-48 tw-animate-pulse"></div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && conversations.length === 0" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8">
      <div class="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
        <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
      </div>
      <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No conversations yet</h3>
      <p class="tw-text-sm tw-text-gray-600 tw-text-center tw-max-w-sm">
        Start connecting with other users to see your conversations here.
      </p>
    </div>

    <!-- Conversations List -->
    <div v-else class="tw-flex-1 tw-overflow-y-auto">
      <div class="tw-space-y-1">
        <div
          v-for="conversation in sortedConversations"
          :key="conversation.id"
          @click="selectConversation(conversation)"
          class="tw-flex tw-items-center tw-gap-3 tw-p-4 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer tw-border-b tw-border-gray-100 last:tw-border-b-0"
          :class="{ 'tw-bg-primary-50 tw-border-primary-200': selectedConversation?.id === conversation.id }"
        >
          <!-- User Avatar -->
          <div class="tw-relative tw-flex-shrink-0">
            <img
              v-if="conversation.other_user?.profile_image"
              :src="conversation.other_user.profile_image"
              :alt="conversation.other_user.name"
              class="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-100"
            />
            <div
              v-else
              class="tw-w-12 tw-h-12 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-gray-100"
            >
              <icon icon="solar:user-bold-duotone" class="tw-text-xl tw-text-gray-400" />
            </div>
            
            <!-- Online Status Indicator -->
            <div
              v-if="conversation.other_user?.is_active"
              class="tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-bg-green-500 tw-rounded-full tw-border-2 tw-border-white"
            ></div>
          </div>

          <!-- Conversation Details -->
          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
              <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate">
                {{ conversation.other_user?.name || 'Unknown User' }}
              </h3>
              <span class="tw-text-xs tw-text-gray-500 tw-flex-shrink-0 tw-ml-2">
                {{ formatTimestamp(getConversationTimestamp(conversation)) }}
              </span>
            </div>
            
            <div class="tw-flex tw-items-center tw-justify-between">
              <p class="tw-text-sm tw-truncate tw-flex-1 tw-text-gray-600">
                <span v-if="getLastMessagePreview(conversation).isCurrentUser" class="tw-text-green-600">{{ t('common.you') }}: </span>
                {{ getLastMessagePreview(conversation).messageContent }}
              </p>
              
              <!-- Unread Count Badge -->
              <div v-if="conversation.unread_count > 0" class="tw-ml-2 tw-flex-shrink-0">
                <span class="tw-bg-primary-500 tw-text-white tw-text-xs tw-font-semibold tw-px-2 tw-py-0 tw-rounded-full tw-min-w-[20px] tw-text-center">
                  {{ conversation.unread_count > 99 ? '99+' : conversation.unread_count }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8">
      <div class="tw-w-16 tw-h-16 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
        <icon icon="solar:warning-bold-duotone" class="tw-text-2xl tw-text-red-500" />
      </div>
      <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Failed to load conversations</h3>
      <p class="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-4">
        {{ error }}
      </p>
      <a-button @click="fetchConversations" intent="primary" size="sm">
        Try Again
      </a-button>
    </div>
  </div>
</template>

<script setup>
import messagingApi from '@/api/messages';
import { ref, onMounted, onUnmounted, defineProps, defineEmits, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores';
import realtimeMessagingService from '@/services/realtime-messaging';

// Props
const props = defineProps({
  selectedConversationId: {
    type: String,
    default: null
  }
});

// Emits
const emit = defineEmits(['conversation-selected', 'conversation-updated']);

// Router
const router = useRouter();

// I18n and Auth
const { t } = useI18n();
const authStore = useAuthStore();

// Reactive data
const conversations = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedConversation = ref(null);

// Computed properties
const sortedConversations = computed(() => {
  return [...conversations.value].sort((a, b) => {
    // Get the timestamp for comparison
    const getTimestamp = (conversation) => {
      // Use last_message.created_at if available (most accurate)
      if (conversation.last_message?.created_at) {
        return new Date(conversation.last_message.created_at).getTime();
      }
      // Fallback to conversation.updated_at
      if (conversation.updated_at) {
        return new Date(conversation.updated_at).getTime();
      }
      // Fallback to conversation.created_at
      if (conversation.created_at) {
        return new Date(conversation.created_at).getTime();
      }
      return 0;
    };

    const timestampA = getTimestamp(a);
    const timestampB = getTimestamp(b);

    // Sort in descending order (most recent first)
    return timestampB - timestampA;
  });
});

// Methods
const fetchConversations = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await messagingApi.conversations.paginate();
    
    if (response.data?.success && response.data?.data) {
      conversations.value = response.data.data;
      
      // Set selected conversation if provided
      if (props.selectedConversationId) {
        selectedConversation.value = conversations.value.find(
          conv => conv.id === props.selectedConversationId
        );
      }
    } else {
      throw new Error('Invalid response format');
    }
  } catch (err) {
    console.error('Error fetching conversations:', err);
    error.value = err.response?.data?.message || 'Failed to load conversations';
  } finally {
    loading.value = false;
  }
};

const refreshConversations = () => {
  fetchConversations();
};

const selectConversation = (conversation) => {
  selectedConversation.value = conversation;
  
  // Navigate to the conversation using router
  router.push({
    name: 'shared.messages',
    params: { id: conversation.id }
  });
  
  // Keep emit for backward compatibility
  emit('conversation-selected', conversation);
  
  // Mark as read if there are unread messages
  if (conversation.unread_count > 0) {
    markAsRead(conversation.id);
  }
};

const markAsRead = async (conversationId) => {
  try {
    await messagingApi.conversations.markAsRead(conversationId);
    
    // Update local state
    const conversation = conversations.value.find(conv => conv.id === conversationId);
    if (conversation) {
      conversation.unread_count = 0;
      emit('conversation-updated', conversation);
    }
  } catch (err) {
    console.error('Error marking conversation as read:', err);
  }
};

const getConversationTimestamp = (conversation) => {
  // Use last_message.created_at if available (most accurate)
  if (conversation.last_message?.created_at) {
    return conversation.last_message.created_at;
  }
  // Fallback to conversation.updated_at
  if (conversation.updated_at) {
    return conversation.updated_at;
  }
  // Fallback to conversation.created_at
  if (conversation.created_at) {
    return conversation.created_at;
  }
  return null;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      // Check if the timestamp has a timezone indicator (Z suffix)
      const isUTC = typeof timestamp === 'string' && timestamp.includes('Z');
      
      if (isUTC) {
        // For UTC timestamps, use UTC methods to avoid timezone conversion
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        return `${displayHours}:${displayMinutes} ${ampm}`;
      } else {
        // For local timestamps, use local methods
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
      }
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      // Check if the timestamp has a timezone indicator (Z suffix)
      const isUTC = typeof timestamp === 'string' && timestamp.includes('Z');
      
      if (isUTC) {
        // For UTC timestamps, use UTC methods to avoid timezone conversion
        const month = date.toLocaleString('en-US', { month: 'short' });
        const day = date.getUTCDate();
        return `${month} ${day}`;
      } else {
        // For local timestamps, use local methods
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

const isCurrentUser = (senderId) => {
  return authStore.user?.id === senderId;
};

const getLastMessagePreview = (conversation) => {
  // Use last_message field if available (from global updates)
  if (conversation.last_message) {
    const messageContent = conversation.last_message.content || 'No message content';
    
    // Check if the last message is from the current user
    // We can determine this by checking if sender_name matches current user name
    // or by checking if the sender_id matches current user id
    const currentUser = authStore.user;
    const isFromCurrentUser = conversation.last_message.sender_name === currentUser?.name || 
                              conversation.last_message.sender_id === currentUser?.id;
    
    return {
      messageContent: messageContent,
      isCurrentUser: isFromCurrentUser
    };
  }
  
  // Fallback to messages array if last_message is not available
  if (conversation.messages && conversation.messages.length > 0) {
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    const messageContent = lastMessage.content || 'No message content';
    
    // Check if the last message is from the current user
    if (isCurrentUser(lastMessage.sender_id)) {
      return {
        messageContent: messageContent,
        isCurrentUser: true
      };
    }
    
    return {
      messageContent: messageContent,
      isCurrentUser: false
    };
  }
  
  return {
    messageContent: 'No messages yet',
    isCurrentUser: false
  };
};

// Global messages handling
const setupGlobalMessages = async () => {
  try {
    await realtimeMessagingService.joinGlobalMessages();
    
    // Listen for global conversation updates
    realtimeMessagingService.on('global-conversation-updated', (conversation) => {
      updateConversationInList(conversation);
    });
    
    // Listen for new conversations
    realtimeMessagingService.on('new-conversation', (conversation) => {
      addConversationToList(conversation);
    });
    
    // Listen for unread count updates
    realtimeMessagingService.on('unread-count-updated', ({ conversation_id, unread_count }) => {
      updateUnreadCount(conversation_id, unread_count);
    });
    
    console.log('Global messages channel setup completed');
  } catch (error) {
    console.error('Error setting up global messages:', error);
  }
};

const updateConversationInList = (updatedConversation) => {
  const index = conversations.value.findIndex(conv => conv.id === updatedConversation.id);
  if (index !== -1) {
    // Update the conversation with new data
    conversations.value[index] = { ...conversations.value[index], ...updatedConversation };
    
    // Emit update event
    emit('conversation-updated', updatedConversation);
  }
};

const addConversationToList = (newConversation) => {
  // Check if conversation already exists
  const existingIndex = conversations.value.findIndex(conv => conv.id === newConversation.id);
  if (existingIndex === -1) {
    // Add new conversation to the top
    conversations.value.unshift(newConversation);
    
    // Emit new conversation event
    emit('conversation-selected', newConversation);
  }
};

const updateUnreadCount = (conversationId, unreadCount) => {
  const conversation = conversations.value.find(conv => conv.id === conversationId);
  if (conversation) {
    conversation.unread_count = unreadCount;
    
    // Emit update event
    emit('conversation-updated', conversation);
  }
};

const cleanupGlobalMessages = () => {
  realtimeMessagingService.leaveGlobalMessages();
  realtimeMessagingService.off('global-conversation-updated');
  realtimeMessagingService.off('new-conversation');
  realtimeMessagingService.off('unread-count-updated');
};

// Lifecycle
onMounted(async () => {
  await fetchConversations();
  await setupGlobalMessages();
});

onUnmounted(() => {
  cleanupGlobalMessages();
});
</script>

<style scoped>
/* Custom scrollbar for conversation list */
.tw-overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.tw-overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>